import { useState } from "react";

import isNil from "lodash/isNil";

import { BookingAnalyticsEvent } from "@/constants/analytics/Analytics";
import { BookingStepBaseTestIDMap } from "@/constants/Booking";
import useBookingAnalytics from "@/hooks/useBookingAnalytics";
import useBookingNavigation from "@/hooks/useBookingNavigation";
import { useBookingDispatch } from "@/store/reducers/booking";
import AvailabilityTimeSlotList from "@/ui/components/schedule/booking/availability/AvailabilityTimeSlotList";
import BookingModalFooter from "@/ui/components/schedule/booking/BookingModalFooter";
import type { BaseBookingStepProps } from "@/ui/components/schedule/booking/BookingStepMap";
import BookingStepTitle from "@/ui/components/schedule/booking/BookingStepTitle";

const AppointmentAvailabilityStep = ({ step }: BaseBookingStepProps) => {
  const baseTestID = BookingStepBaseTestIDMap[step];

  const ListHeaderComponent = <BookingStepTitle step={step} />;

  const { setTimeRange } = useBookingDispatch();
  const { onBackwardNavigation, onForwardNavigation } = useBookingNavigation({
    currentStep: step,
  });

  const [timeSlot, setTimeSlot] = useState<TimeSlot | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isForwardNavigationDisabled = isNil(timeSlot) && !isLoading;

  const { trackEvent } = useBookingAnalytics();

  const handleForwardNavigation = () => {
    if (isNil(timeSlot)) {
      return;
    }

    setTimeRange({
      endTime: timeSlot.end,
      startTime: timeSlot.start,
    });

    onForwardNavigation();
  };

  const handleTimeSlotSelected = (t?: TimeSlot) => {
    if (!isNil(t)) {
      trackEvent({
        event: BookingAnalyticsEvent.AvailabilitySelection,
        source: step,
      });
    }
    setTimeSlot(t);
  };

  const handleLoadingChange = (isListLoading: boolean) => {
    setIsLoading(isListLoading);
  };

  return (
    <>
      <AvailabilityTimeSlotList
        ListHeaderComponent={ListHeaderComponent}
        onLoadingChange={handleLoadingChange}
        onTimeSlotSelect={handleTimeSlotSelected}
        testID={baseTestID}
      />
      <BookingModalFooter
        isForwardNavigationDisabled={isForwardNavigationDisabled}
        isLoading={isLoading}
        onBackwardPress={onBackwardNavigation}
        onForwardPress={handleForwardNavigation}
        step={step}
      />
    </>
  );
};

export default AppointmentAvailabilityStep;
